/**
 * The product chrome the tour puts at rest: dimmed and inert while it runs.
 * Looks only; what responds is decided by the guard, which denies by default,
 * so a selector that stops matching un-dims something and unblocks nothing.
 * `from`/`to` are the steps a selector is on screen for; the browser suite
 * fails when one matches nothing there. No imports: that suite reads this.
 */

export interface ChromeRule {
  name: string;
  selector: string;
  from: number;
  to: number;
}

export const CHROME: readonly ChromeRule[] = [
  { name: "top bar", selector: "#root header", from: 1, to: 10 },
  {
    name: "account menu",
    selector: '#root button[aria-label="Account"]',
    from: 1,
    to: 10,
  },
  {
    name: "canvas toolbars",
    selector: '.react-flow [role="toolbar"]',
    from: 1,
    to: 7,
  },
  {
    name: "canvas share",
    selector: '.react-flow button[aria-label^="Share"]',
    from: 1,
    to: 7,
  },
  {
    name: "chat header actions",
    selector:
      '[data-floating-chat="window"] :is(button[aria-label="Start new chat"], button[aria-label="Expand to full height"], button[aria-label="Minimize"])',
    from: 4,
    to: 7,
  },
  {
    name: "chat resize handles",
    selector: '[data-floating-chat="window"] [role="separator"]',
    from: 4,
    to: 7,
  },
  {
    name: "deck pane actions",
    selector: ".js-right-pane-panel button:not([data-onboarding-target])",
    from: 8,
    to: 9,
  },
];

/** The deck pane's divider: dragging it is always allowed. */
export const RESIZE_SELECTOR = '.js-right-pane-panel > [role="separator"]';
/** What the product lays over the window while a divider is dragged. */
const RESIZE_OVERLAY_SELECTOR = "[data-resize-drag-overlay]";

/**
 * Chrome the card may have to stand on on a short window. Under the card it
 * is put away whole rather than left cut by the card's edge.
 */
export const COVERABLE = {
  controls:
    '.react-flow :is([role="toolbar"][aria-label="Canvas controls"], button[aria-label^="Share"])',
  tools: '.react-flow [role="toolbar"][aria-label="Canvas tools"]',
} as const;
export const COVERS_ATTR = "data-co-tour-covers";

/**
 * Product overlays the tour hides: they open over the canvas, on top of the
 * tour's nodes, and nothing in them responds.
 */
export const HIDDEN: readonly ChromeRule[] = [
  {
    name: "node toolbar",
    selector: ".canvas-floating-toolbar-portal",
    from: 4,
    to: 5,
  },
];

/** Marks the document while the guard is installed. */
export const GUARD_ATTR = "data-co-tour";
/** Marks the tour's own surfaces; the guard lets everything in them through. */
export const TOUR_UI_ATTR = "data-co-tour-ui";
/** Marks the document while the pointer is on something the canvas allows. */
export const HOT_ATTR = "data-co-tour-hot";

const PRESSABLE = ':is(button, [role="button"], a[href])';
const ENABLED = ':not(:disabled, [aria-disabled="true"])';

/**
 * Whether a point on screen is on chrome at rest. It takes no pointer, so a
 * press on it reaches whatever lies beneath.
 */
export function chromeAt(x: number, y: number): boolean {
  return CHROME.some((rule) =>
    [...document.querySelectorAll(rule.selector)].some((el) => {
      const r = el.getBoundingClientRect();
      return (
        r.width > 0 &&
        r.height > 0 &&
        x >= r.left &&
        x <= r.right &&
        y >= r.top &&
        y <= r.bottom
      );
    }),
  );
}

export function chromeStyles(): string {
  const scope = `html[${GUARD_ATTR}]`;
  const dim = CHROME.map((rule) =>
    rule.selector
      .split(/,\s*(?![^(]*\))/)
      .map((s) => `${scope} ${s}`)
      .join(",\n"),
  ).join(",\n");
  return `
${dim} {
  opacity: 0.38;
  filter: saturate(0.4);
  pointer-events: none !important;
  transition: opacity 200ms ease;
}
${Object.entries(COVERABLE)
  .map(
    ([name, selector]) =>
      `html[${GUARD_ATTR}][${COVERS_ATTR}~="${name}"] ${selector} { opacity: 0; }`,
  )
  .join("\n")}
${HIDDEN.map((rule) => `${scope} ${rule.selector}`).join(",\n")} {
  display: none !important;
}
${scope} .react-flow__node,
${scope} .react-flow__node * {
  pointer-events: none !important;
}
/* What responds shows the pointer; everything else keeps the arrow. */
${scope} body * {
  cursor: default !important;
}
${scope} [${TOUR_UI_ATTR}] ${PRESSABLE}${ENABLED},
${scope} [${TOUR_UI_ATTR}] ${PRESSABLE}${ENABLED} *,
${scope}[${HOT_ATTR}] .react-flow__pane,
${scope}[${HOT_ATTR}] .react-flow__pane * {
  cursor: pointer !important;
}
${scope} ${RESIZE_SELECTOR},
${scope} ${RESIZE_SELECTOR} *,
${scope} ${RESIZE_OVERLAY_SELECTOR} {
  cursor: col-resize !important;
}
`;
}

/** The pointer on the product controls a scene lets through. */
export function passStyles(pass: readonly string[]): string {
  if (!pass.length) return "";
  const scope = `html[${GUARD_ATTR}]`;
  return `${pass
    .flatMap((selector) => [
      `${scope} ${selector}${PRESSABLE}${ENABLED}`,
      `${scope} ${selector}${PRESSABLE}${ENABLED} *`,
      `${scope} ${selector} ${PRESSABLE}${ENABLED}`,
      `${scope} ${selector} ${PRESSABLE}${ENABLED} *`,
    ])
    .join(",\n")} {
  cursor: pointer !important;
}`;
}
